// src/MobileExperience.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTrueViewportHeight } from './hooks/useTrueViewportHeight';
import D3Cloud from "./mobile/D3Cloud";
import questionData from "./data/questionData";
import FeatureScroll from './mobile/FeatureScroll';
import ProgressIndicator from './components/ProgressIndicator';
import { headlineFlowData } from './data/headlineData';
import "./Styles.css";
import ErrorBoundary from './components/ErrorBoundary';
import NavigationButtons from './mobile/NavigationButtons';
import TransitionCard from './mobile/TransitionCard';
import SingleHeadlineDisplay from './mobile/SingleHeadlineDisplay';
import ReturnToTop from './components/ReturnToTop';
import { useMobileAppStore } from './mobileStore';

// --- CONSTANTS ---
const NUM_QUESTION_SECTIONS = questionData.length;
const NUM_FEATURE_SECTIONS = 9;
const NUM_HEADLINE_SECTIONS = headlineFlowData.length;

// Mobile-specific section heights
const MOBILE_SECTION_HEIGHTS = {
  intro: 100,
  question: 120,
  returnPhase: 60,
  neuronalConnectionsTimeline: 300,
  neuronalConnectionsTrigger: 200,
  feature: 150,
  headlineFlowStartTrigger: 1,
  headlineFlowScrollArea: 400, // Fixed height for mobile
};

const MobileExperience: React.FC = () => {
  useTrueViewportHeight();
  
  // Mobile-specific state
  const [previousActiveSection, setPreviousActiveSection] = useState<number | null>(null);
  const [mobileTransition, setMobileTransition] = useState<'answers' | 'stories' | null>(null);
  const [areButtonsVisible, setAreButtonsVisible] = useState(false);
  const [isCloudVisible, setIsCloudVisible] = useState(true);

  // Store state
  const activeSection = useMobileAppStore((state) => state.activeSection);
  const appMode = useMobileAppStore((state) => state.appMode);
  const isInteractionReady = useMobileAppStore((state) => state.isInteractionReady);
  const storeActions = useMobileAppStore((state) => state.actions);

  // Section indices
  const featureStartIndexGlobal = NUM_QUESTION_SECTIONS + 3;
  const lastFeatureIndexGlobal = featureStartIndexGlobal + NUM_FEATURE_SECTIONS - 1;
  const headlineStartIndexGlobal = lastFeatureIndexGlobal + 1;
  const lastHeadlineIndexGlobal = headlineStartIndexGlobal + NUM_HEADLINE_SECTIONS - 1;

  // Mobile initialization
  useEffect(() => {
    const buttonFadeInTimer = setTimeout(() => setAreButtonsVisible(true), 5500);
    
    // Disable scroll for mobile step-based navigation
    const shouldDisableScroll = appMode !== 'intro' && appMode !== 'viewing_headlines';
    if (shouldDisableScroll) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    
    return () => {
      clearTimeout(buttonFadeInTimer);
      document.body.classList.remove('no-scroll');
    };
  }, [appMode]);

  // App mode management
  useEffect(() => {
    if (appMode === 'transitioning_to_features' || appMode === 'showing_stories_card') return;
    if (activeSection === null) return;
    
    if (activeSection >= 1 && activeSection <= NUM_QUESTION_SECTIONS + 1) {
      storeActions.setAppMode('answering_questions');
    } else if (activeSection >= featureStartIndexGlobal && activeSection <= lastFeatureIndexGlobal) {
      storeActions.setAppMode('viewing_features');
    } else if (activeSection >= headlineStartIndexGlobal) {
      storeActions.setAppMode('viewing_headlines');
    }
  }, [activeSection, appMode, storeActions, featureStartIndexGlobal, lastFeatureIndexGlobal, headlineStartIndexGlobal]);

  const handlePreviousSection = useCallback(() => {
    if (mobileTransition || appMode === 'showing_stories_card') return;
    
    const targetSection = activeSection === null ? lastFeatureIndexGlobal : activeSection - 1;
    if (targetSection >= 0) {
      storeActions.setNavigating(true);
      setPreviousActiveSection(activeSection);
      storeActions.setSectionFromButton(targetSection);
      if (targetSection < featureStartIndexGlobal) {
        setIsCloudVisible(true);
      }
      setTimeout(() => storeActions.setNavigating(false), 500);
    }
  }, [activeSection, appMode, mobileTransition, storeActions, featureStartIndexGlobal, lastFeatureIndexGlobal]);

  const handleNextSection = useCallback(() => {
    // Handle the click WHILE the "answers" card is showing
    if (mobileTransition === 'answers') {
      setIsCloudVisible(false);
      setMobileTransition(null);
      storeActions.handleNextOnLastQuestion([], window.innerHeight, true);
      setTimeout(() => storeActions.completeTransition(), 800);
      return;
    }
    
    // Handle the click WHILE the "stories" card is showing
    if (appMode === 'showing_stories_card') {
      storeActions.setAppMode('viewing_headlines');
      storeActions.setSectionFromButton(headlineStartIndexGlobal);
      return;
    }
    
    if (useMobileAppStore.getState().isNavigating) return;

    const lastQuestionSection = NUM_QUESTION_SECTIONS;
    const lastFeatureSection = lastFeatureIndexGlobal;
    
    storeActions.setNavigating(true);
    if (activeSection !== null) {
      setPreviousActiveSection(activeSection);
    }

    if (activeSection === lastFeatureSection) {
      // When on the last feature, switch to the card-only mode and PAUSE
      storeActions.setAppMode('showing_stories_card');
    } else if (activeSection === null || activeSection < lastHeadlineIndexGlobal) {
      if (activeSection === lastQuestionSection) {
        // When on the last question, show the "answers" card and PAUSE
        storeActions.setSectionFromButton(lastQuestionSection + 1);
        setMobileTransition('answers');
      } else {
        // Standard advance for all other steps
        storeActions.setSectionFromButton(activeSection === null ? 0 : activeSection + 1);
      }
    }
    
    setTimeout(() => storeActions.setNavigating(false), 500);
  }, [activeSection, appMode, mobileTransition, storeActions, headlineStartIndexGlobal, lastHeadlineIndexGlobal, lastFeatureIndexGlobal]);

  const handleNeuronalAnimationComplete = useCallback(() => {
    storeActions.completeTransition();
  }, [storeActions]);

  return (
    <div className="app" style={{ height: '100vh', overflow: 'hidden' }}>
      <ErrorBoundary>
        <D3Cloud
          questionData={questionData}
          activeSection={activeSection ?? 0}
          scrollY={0}
          onCloudExitComplete={handleNeuronalAnimationComplete}
          neuronalSectionStartVh={0}
          neuronalSectionHeightVh={MOBILE_SECTION_HEIGHTS.neuronalConnectionsTimeline}
          scrollDirection={'down'}
          onNeuronalAnimationComplete={handleNeuronalAnimationComplete}
          isCloudVisible={isCloudVisible}
        />
        
        {/* Progress Indicators */}
        <div style={{ 
          position: 'fixed', top: '20px', left: '20px', zIndex: 5,
          display: 'flex', flexDirection: 'column', gap: '10px'
        }}>
          <ProgressIndicator 
            title="Questions" 
            sections={questionData.map((c) => ({ id: `q-pill-${c.id}`, color: c.backgroundColor }))}
            activeSectionGlobal={activeSection ?? 0}
            startSectionGlobalIndex={1}
            endSectionGlobalIndex={NUM_QUESTION_SECTIONS + 2}
            visibilityAndTrackingStartIndex={0}
            progressMode="perSection"
          />
          <ProgressIndicator 
            title="Answers"
            sections={Array.from({ length: NUM_FEATURE_SECTIONS }).map((_, i) => ({ 
              id: `f-pill-${i}`, 
              color: questionData[i % NUM_QUESTION_SECTIONS]?.backgroundColor || '#cccccc' 
            }))}
            activeSectionGlobal={activeSection ?? 0}
            startSectionGlobalIndex={featureStartIndexGlobal}
            endSectionGlobalIndex={lastFeatureIndexGlobal}
            visibilityAndTrackingStartIndex={featureStartIndexGlobal - 1}
            isActive={(activeSection ?? 0) >= featureStartIndexGlobal}
            progressMode="perSection"
          />
          <ProgressIndicator 
            title="Stories"
            sections={headlineFlowData.map((h, i) => ({ 
              id: `h-pill-${i}`, 
              color: questionData[i % NUM_QUESTION_SECTIONS]?.backgroundColor || '#dddddd' 
            }))}
            activeSectionGlobal={activeSection ?? 0}
            startSectionGlobalIndex={headlineStartIndexGlobal}
            endSectionGlobalIndex={lastHeadlineIndexGlobal}
            visibilityAndTrackingStartIndex={lastFeatureIndexGlobal}
            progressMode="perSection"
          />
        </div>

        <FeatureScroll
          scrollY={0}
          activeSection={activeSection ?? 0}
          previousActiveSection={previousActiveSection}
          viewportHeight={window.innerHeight}
          sectionStartVhValues={[]}
          isVisible={appMode === 'viewing_features' || appMode === 'transitioning_to_features'}
          isInteractionReady={isInteractionReady}
        />
        
        {/* Mobile Headlines */}
        {appMode === 'viewing_headlines' && headlineFlowData.map((headline, index) => {
          const sectionIndex = headlineStartIndexGlobal + index;
          const isCurrentlyActive = activeSection === sectionIndex;
          const wasPreviouslyActive = previousActiveSection === sectionIndex;
          
          if (!isCurrentlyActive && !wasPreviouslyActive) return null;
          
          return (
            <SingleHeadlineDisplay 
              key={headline.id}
              imageSrc={headline.imageSrc}
              isActive={isCurrentlyActive}
            />
          );
        })}
        
        <NavigationButtons 
          currentSection={activeSection ?? 0}
          onPrevious={handlePreviousSection}
          onNext={handleNextSection}
          isVisible={areButtonsVisible && (appMode !== 'viewing_headlines' || (activeSection ?? 0) < lastHeadlineIndexGlobal)}
          totalSections={lastHeadlineIndexGlobal + 1}
        />

        <TransitionCard 
          text="And then looking for answers..." 
          isVisible={mobileTransition === 'answers'} 
          displayStyle="fade-in-out" 
        />
        <TransitionCard 
          text="Then sharing stories" 
          isVisible={appMode === 'showing_stories_card'} 
          displayStyle="fade-in-out" 
        />
        
        <div style={{ 
          position: 'fixed', bottom: '40px', width: '100%',
          display: 'flex', justifyContent: 'center', zIndex: 1000, 
          pointerEvents: 'auto'
        }}>
          <ReturnToTop 
            isVisible={(activeSection ?? 0) === lastHeadlineIndexGlobal}
            onClick={() => { 
              window.scrollTo(0, 0); 
              setTimeout(() => window.location.reload(), 50); 
            }}
          />
        </div>

      </ErrorBoundary>
    </div>
  );
};

export default MobileExperience;